import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useDrag } from '@use-gesture/react'
import { useSpring, useTransition, useSpringRef, a, config } from '@react-spring/web'

import './Instructions.css'

import { useGame } from '../../hooks'

export default function Instructions({ open, onClose: onCloseProp }) {
  const timeoutRef = useRef()
  const [click, set] = useState(0)

  const { t } = useTranslation('translations')
  const start = useGame(state => state.start)

  const styles = useSpring({
    y: click === 1 ? 20 : click === 2 ? 90 : -20,
    config: config.stiff,
  })

  const closeStyles = useSpring({
    y: click === 2 ? 80 : -10,
    opacity: click === 2 ? 1 : 0,
    config: config.stiff,
  })

  const transRef = useSpringRef()
  const transitions = useTransition(click, {
    ref: transRef,
    from: { opacity: 0, transform: 'translate3d(16px,0,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: { opacity: 0, transform: 'translate3d(-16px,0,0)' },
    config: config.gentle,
  })

  const mount = useTransition(open, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.slow,
  })

  useEffect(() => {
    transRef.start()
  }, [click])

  useEffect(() => {
    if (click === 2) {
      timeoutRef.current = setTimeout(() => {
        onCloseProp()
        start()
      }, 5000)
    }
  }, [click, open])

  const onDrag = ({ down, movement: [mx], cancel }) => {
    if (down) return
    const sign = mx < 0 ? 1 : -1

    set(state => {
      if (state + sign < 0) {
        return 3 - 1
      } else {
        return (state + sign) % 3
      }
    })
  }

  const bind = useDrag(onDrag, {
    threshold: 10,
  })

  const onClose = useCallback((e) => {
    e.stopPropagation()
    clearTimeout(timeoutRef.current)
    start()
    onCloseProp()
  }, [start, timeoutRef])

  return mount((style, item) => item && (
    <a.div style={style} className="Instructions" {...bind()}>
      <button className="Instructions__close" onClick={onClose}>
        {/* Close */}
        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 89 89">
          <path className="Instructions__close-path" d="M2 44.359v-.002A42.266 42.266 0 0 1 44.171 2l-.002-1 .005 1A42.36 42.36 0 1 1 2 44.359Z" stroke="#fff" strokeWidth="2"/>
          <path d="M55.328 58.17 44.057 46.897 32.965 57.99l-2.147-2.146L41.91 44.75l-11.33-11.33 2.146-2.147 11.331 11.33 11.032-11.032 2.147 2.147L46.204 44.75l11.271 11.272-2.147 2.146Z" fill="#fff"/>
        </svg>
      </button>
      <div className="Instructions__container">
        <div className="Instructions__lipstick">
          <a.svg style={closeStyles} className="Instructions__cap" fill="none" viewBox="0 0 189 351" xmlns="http://www.w3.org/2000/svg">
            <path id="Path" d="M4.03 3.42 L185.12 3.42 185.12 347.31 4.03 347.31 Z" fill="#b9202d" stroke="none"/>
            <path id="Path-1" d="M189 350.34 L0.16 350.34 0.16 0.41 189 0.41 Z M7.91 344.34 L181.25 344.34 181.25 6.44 7.91 6.44 Z" fill="#ffffff"stroke="none"/>
          </a.svg>
          <a.svg style={styles} className="Instructions__refill" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 212 670">
            <path d="M0 663.3h205.4V546.4h-22.2V249.2h-19.5V63.8c0-31.2-24.7-57.3-55.9-57.8-31.9-.5-57.9 25.1-57.9 56.9v186.3H28.2v297.2H6v116.9" fill="#B9202D"/>
            <path d="M189.1 540.6V243.3h-19.5V63.8c0-34.6-27.7-63.1-61.7-63.7-16.9-.3-33 6.1-45.1 18C50.7 30 44 45.9 44 62.9v180.5H22.3v297.2H.1v128.6h211.3V540.6h-22.3ZM71 26.5c9.8-9.7 22.8-14.9 36.6-14.6 27.6.5 50.1 23.8 50.1 51.9v179.5h-102V62.9c.1-13.8 5.5-26.7 15.3-36.4ZM49.9 255.1h127.4v285.5H34V255.1h15.9Zm149.6 402.3H11.8V552.3H199.6v105.1h-.1Z" fill="#fff"/>
          </a.svg>
          <svg className="Instructions__base" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 257 308">
            <path d="M29.8 6.8H14.4C9.8 6.8 6 10.6 6 15.2l4.7 286.9h235l4.7-286.9c0-4.6-3.8-8.4-8.4-8.4h-15.4" fill="#B9202D"/>
            <path d="M242 .9H14.4C6.5.9.1 7.3.1 15.3L4.9 308h246.6l4.8-292.8c0-7.9-6.4-14.3-14.3-14.3Zm-14.3 11.8H242c1.4 0 2.5 1.1 2.5 2.4l-.7 41.4h-26.7c0-.5-.1-1.1-.1-1.6v-.2l-.3-1.8c-.2-1.2-.5-2.4-.7-3.6-.1-.6-.3-1.1-.4-1.7-.3-1.1-.6-2.3-.9-3.4-.2-.7-.6-1.4-.8-2-.7-2.1-1.7-4.1-2.7-6.1-.4-.8-.8-1.5-1.3-2.2-.9-1.3-1.8-2.6-2.7-3.8-1-1.3-2.1-2.6-3.2-3.8-1.4-1.5-3-2.9-4.6-4.2-1.3-1-2.6-2-4-2.9-1-.7-2.2-1.3-3.3-1.9-1.7-.9-3.5-1.7-5.3-2.5-1.4-.6-2.9-1.1-4.4-1.5-.7-.2-1.3-.4-2-.6h47.3Zm-215 55.6H39c0 .6.1 1.1.2 1.7.2 1.5.4 3.1.7 4.6.2 1.2.5 2.3.8 3.5.3 1.1.6 2.3 1 3.4.6 1.6 1.1 3.2 1.8 4.7.5 1.3 1.1 2.5 1.8 3.7.8 1.5 1.7 3 2.7 4.4.9 1.4 2 2.7 3 4 1.3 1.8 2.9 3.2 4.5 4.7 1.2 1.1 2.6 2.1 3.9 3.1 1.7 1.3 3.5 2.4 5.3 3.3 1.5.8 3.1 1.5 4.7 2.2.1 0 .2.1.2.1H13.5l-.8-43.4Zm67.2-49.6c1.5-1 3.1-2 4.8-2.8 1.2-.6 2.4-1.1 3.7-1.5 1.2-.4 2.5-.8 3.7-1.2.8-.2 1.5-.3 2.3-.5h18.2c.3 0 .5.1.8.1 1.5.3 2.9.8 4.1 1.9 1.2 1.1 1.6 2.5 1.6 4 0 14.4-.1 28.8-.1 43.2v7.4c0 .6.1 1.3.1 1.9 0 1.1-.1 2.3-.1 3.4 0 .6.1 1.1.1 1.7v.4c0 .3-.1.5-.1.8v24.6c0 1 0 2.1-.1 3.1-.1.9-.3 1.8-.5 2.7-.4 1.5-1.6 2.4-3 2.9-1.1.4-2.2.5-3.4.5-3.1.1-6.1 0-9.2 0-1.3 0-2.6-.1-3.9-.2-.6 0-1.2 0-1.8-.1-1-.1-2-.2-3.1-.3-.5 0-1-.1-1.4-.1-.7-.1-1.4-.2-2-.3-.8-.1-1.7-.2-2.5-.4-.9-.2-1.8-.5-2.8-.6-.1 0-.2-.1-.2-.1-.8-.3-1.6-.6-2.5-.9-1.8-.6-3.6-1.5-5.2-2.5-1.1-.7-2.1-1.6-3.1-2.4-.7-.6-1.4-1.3-2.1-2L70.1 99c-.9-1.1-1.9-2.3-2.7-3.5-1.3-2-2.5-4-3.4-6.1-.4-.9-.8-1.7-1.2-2.6-.3-.7-.5-1.5-.8-2.3-.3-.7-.4-1.5-.8-2.3-.2-.4-.2-.9-.4-1.3-.2-.8-.4-1.5-.6-2.3-.3-1.1-.5-2.3-.7-3.4-.2-1-.3-2-.5-3-.2-1.1-.4-2.2-.5-3.3-.1-1.4-.2-2.7-.3-4.1-.1-1.1-.1-2.1-.1-3.2 0-1 .1-2 .2-3 .1-.7.1-1.4.2-2.2.1-1 .3-1.9.5-2.9.2-1.3.5-2.6.8-3.9l.9-3.3c.2-.8.4-1.6.7-2.4.6-1.6 1.3-3.2 1.9-4.8.4-1 .9-2 1.4-3 .5-.9.9-1.7 1.5-2.5 1-1.5 2-3.1 3.1-4.6.8-1.1 1.8-2.2 2.8-3.2.8-.9 1.6-1.8 2.5-2.6.6-.6 1.3-1.2 2-1.8 1.1-1.1 2.1-2 3.3-2.7Zm103.7 83.7c-1.7 1.6-3.5 3.1-5.6 4.3-1.5.9-3.1 1.5-4.8 2.1-1.9.7-3.9 1.1-5.9 1.4-1.5.2-3 .5-4.6.7-1 .1-2.1.1-3.2.2l-5.4.3c-.5 0-1 .1-1.5.1h-5.7c-1.4 0-2.8-.1-4.2-.3-.7-.1-1.4-.2-2-.5-.9-.4-1.5-1-2-1.8-.6-1-.9-2.1-.9-3.2 0-.8-.1-1.6-.1-2.5 0-28-.1-56-.1-84 0-1.5.4-2.8 1.6-3.8.8-.7 1.7-1.1 2.7-1.3.6-.1 1.2-.3 1.8-.4.7-.1 1.4-.3 2.1-.3 1.4-.1 2.8-.2 4.1-.2v.1c.8 0 1.5-.1 2.3-.1 1.1 0 2.2 0 3.3.1 1.4.1 2.7.3 4.1.5 1.4.2 2.8.4 4.1.7 1.1.2 2.2.6 3.3.9.8.2 1.5.5 2.3.8 1.9.7 3.8 1.5 5.6 2.5 1.4.8 2.8 1.6 4.1 2.5.8.6 1.6 1.2 2.4 1.9l3.3 3c1.5 1.5 2.8 3.2 4 5 1.4 2 2.6 4.1 3.6 6.2.9 1.9 1.7 3.9 2.5 5.9.6 1.7 1.1 3.4 1.6 5.2.4 1.3.7 2.6.9 3.9.2 1.3.3 2.7.6 4 .3 1.8.4 3.6.3 5.4v3.7c-.1 1.2-.2 2.4-.3 3.7-.1.8-.1 1.6-.2 2.4-.1.6-.2 1.1-.3 1.7-.2 1.2-.4 2.4-.7 3.6-.2 1.2-.5 2.3-.8 3.5-.5 1.6-1 3.3-1.6 4.9-.4 1.2-.9 2.5-1.4 3.7-.6 1.4-1.4 2.8-2.1 4.1-.8 1.4-1.8 2.8-2.7 4.1-.8 1.1-1.7 2.2-2.6 3.3-.6.6-1.2 1.3-1.9 2Zm3.1 9.4c1.4-.7 2.9-1.2 4.4-1.9 2-.9 3.8-2.1 5.6-3.3 1.2-.9 2.3-1.8 3.4-2.7.6-.5 1.3-1 1.9-1.6.7-.7 1.4-1.4 2.1-2.2.8-.9 1.6-1.7 2.3-2.6.9-1.2 1.7-2.4 2.5-3.6.5-.8 1-1.5 1.5-2.3.6-1.1 1.2-2.3 1.8-3.5.6-1.2 1.1-2.4 1.6-3.6.4-.9.7-1.8 1-2.7.2-.6.5-1.2.6-1.9.4-1.8.9-3.6 1.3-5.4.2-1.1.5-2.3.6-3.4.1-.9.2-1.7.2-2.6h26.3l-.7 43.5-56.4-.2c-.1 0-.1 0 0 0ZM14.4 12.7h61.5c-.2.1-.4.1-.6.2-1.5.5-2.9 1-4.3 1.5-.2.1-.5.2-.7.3-.7.3-1.4.6-2.2.9-.5.2-.9.4-1.4.7-.6.3-1.2.5-1.7.8-.8.5-1.6 1-2.5 1.4-.7.4-1.5.8-2.2 1.3-.9.6-1.7 1.2-2.5 1.9-1.4 1.2-2.9 2.4-4.3 3.7-.7.6-1.3 1.3-2 2-.6.6-1.2 1.3-1.7 2-1 1.4-2.1 2.8-3 4.3-1 1.5-1.8 3.2-2.7 4.8-.6 1.2-1.1 2.4-1.6 3.7-.5 1.4-1 2.7-1.4 4.1-.4 1.5-.8 3-1.1 4.6-.5 1.9-.8 3.8-.9 5.8H12.4l-.7-41.4c.2-1.5 1.3-2.6 2.7-2.6Zm2.1 283.5-2.8-171.6h229.1L240 296.2H16.5Z" fill="#fff"/>
          </svg>
        </div>
      </div>
      <div className="Instructions__label">
        {transitions((style, i) => {
          return <a.div style={{ ...style, position: 'absolute' }}>{ t(`instructions.step${click}`) }</a.div>
        })}
      </div>
      <ul className="Instructions__nav">
        {
          Array(3).fill().map((_, index) => (
            <li key={index} className={classNames({ 'is-active': click === index })}></li>
          ))
        }
      </ul>
    </a.div>
  ))
}
